import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import { CheckSquare, Camera, ChevronLeft, Loader2, AlertCircle, CheckCircle2, MapPin, Building2 } from 'lucide-react';

export default function ChecklistForm() {
  const { uuid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [cevaplar, setCevaplar] = useState({});
  const [notlar, setNotlar] = useState('');
  const [fotograflar, setFotograflar] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/checklistler/form/${uuid}`)
      .then(({ data }) => {
        setForm(data);
        const init = {};
        data.sorular.forEach(s => {
          init[s.id] = { soru_id: s.id, evet_hayir: null, puan: null, not_metni: '' };
        });
        setCevaplar(init);
      })
      .catch(err => setError(err.response?.data?.error || 'Bağlantı hatası'));
  }, [uuid]);

  const setCevap = (id, field, val) => {
    setCevaplar(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  };

  const handleFileChange = e => {
    setFotograflar(Array.from(e.target.files || []).slice(0, 5));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    for (const s of form.sorular) {
      if (!s.zorunlu) continue;
      const c = cevaplar[s.id];
      if (s.soru_tipi === 'evet_hayir' && c?.evet_hayir === null) return toast.error(`"${s.soru_metni}" zorunludur`);
      if (s.soru_tipi === 'puan' && !c?.puan) return toast.error(`"${s.soru_metni}" zorunludur`);
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('notlar', notlar);
      fd.append('cevaplar', JSON.stringify(Object.values(cevaplar)));
      fotograflar.forEach(f => fd.append('fotograflar', f));
      await api.post(`/checklistler/kontrol/${uuid}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Gönderilemedi');
    } finally { setSending(false); }
  };

  if (done) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 24, padding: 48, textAlign: 'center', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <CheckCircle2 size={36} color="#fff" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Kontrol Kaydedildi!</h2>
        <p style={{ color: '#475569', fontSize: 14, margin: '0 0 24px' }}>Checklist kontrolünüz başarıyla kaydedildi.</p>
        <button className="btn btn-primary" onClick={() => navigate('/checklist-kayitlar')}>Kayıtlara Dön</button>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 40, textAlign: 'center', maxWidth: 360 }}>
        <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
        <h2 style={{ margin: '0 0 8px' }}>Hata</h2>
        <p style={{ color: '#475569', margin: '0 0 20px' }}>{error}</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>Geri Dön</button>
      </div>
    </div>
  );

  if (!form) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner spinner-blue" style={{ width: 36, height: 36 }} />
    </div>
  );

  const tamamlandi = form.sorular.filter(s => {
    const c = cevaplar[s.id];
    return (c?.evet_hayir !== null && c?.evet_hayir !== undefined) || c?.puan;
  }).length;
  const ilerleme = Math.round((tamamlandi / form.sorular.length) * 100);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a5f)', padding: '20px 16px 28px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, marginBottom: 16, padding: 0 }}>
            <ChevronLeft size={18} /> Geri
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: '0 0 6px' }}>{form.checklist?.baslik}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                {form.lokasyon?.bina_adi && <><Building2 size={13} /><span>{form.lokasyon.bina_adi} ·</span></>}
                <MapPin size={13} /><span>{form.lokasyon?.lokasyon_adi}</span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>{tamamlandi}/{form.sorular.length}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>tamamlandı</div>
            </div>
          </div>
          <div style={{ marginTop: 14, background: 'rgba(255,255,255,0.15)', borderRadius: 6, height: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${ilerleme}%`, background: 'linear-gradient(90deg,#3b82f6,#06b6d4)', borderRadius: 6, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px 0' }}>
        <form onSubmit={handleSubmit}>
          {form.sorular.map((s, i) => {
            const c = cevaplar[s.id];
            const answered = (c?.evet_hayir !== null && c?.evet_hayir !== undefined) || !!c?.puan;
            return (
              <div key={s.id} className="card" style={{ padding: 20, marginBottom: 12, borderLeft: `4px solid ${answered ? '#10b981' : 'var(--gray-200)'}` }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--navy)' }}>
                  <span style={{ color: 'var(--blue)', marginRight: 8 }}>{i+1}.</span>
                  {s.soru_metni}
                  {s.zorunlu && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                </div>

                {s.soru_tipi === 'evet_hayir' && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ val: true, label: '✓ Evet', ok: true }, { val: false, label: '✗ Hayır', ok: false }].map(opt => (
                      <button key={String(opt.val)} type="button" onClick={() => setCevap(s.id, 'evet_hayir', opt.val)}
                        style={{ flex: 1, padding: '11px', borderRadius: 12, border: `2px solid ${c?.evet_hayir === opt.val ? (opt.ok ? '#16a34a' : '#dc2626') : 'var(--gray-200)'}`, background: c?.evet_hayir === opt.val ? (opt.ok ? '#f0fdf4' : '#fef2f2') : '#fff', color: c?.evet_hayir === opt.val ? (opt.ok ? '#16a34a' : '#dc2626') : 'var(--gray-600)', fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all 0.15s' }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                {s.soru_tipi === 'puan' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1,2,3,4,5].map(p => (
                      <button key={p} type="button" onClick={() => setCevap(s.id, 'puan', p)}
                        style={{ width: 44, height: 44, borderRadius: 10, border: `2px solid ${c?.puan === p ? 'var(--blue)' : 'var(--gray-200)'}`, background: c?.puan === p ? '#eff6ff' : '#fff', color: c?.puan === p ? 'var(--blue-600)' : 'var(--gray-600)', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all 0.15s' }}>
                        {p}
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ marginTop: 10 }}>
                  <input className="input" placeholder="Not ekle... (opsiyonel)" value={c?.not_metni || ''} onChange={e => setCevap(s.id, 'not_metni', e.target.value)} style={{ fontSize: 13 }} />
                </div>
              </div>
            );
          })}

          <div className="card" style={{ padding: 20, marginBottom: 12 }}>
            <label className="input-label" style={{ marginBottom: 8 }}>Genel Notlar</label>
            <textarea className="input" rows={3} placeholder="Genel notlarınız..." value={notlar} onChange={e => setNotlar(e.target.value)} style={{ resize: 'vertical' }} />
          </div>

          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <label className="input-label" style={{ marginBottom: 8 }}>Fotoğraflar (max 5)</label>
            <button type="button" onClick={() => fileRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: '2px dashed var(--gray-200)', background: 'var(--gray-50)', color: 'var(--gray-600)', cursor: 'pointer', fontSize: 14, width: '100%', justifyContent: 'center' }}>
              <Camera size={16} />
              {fotograflar.length > 0 ? `${fotograflar.length} fotoğraf seçildi` : 'Fotoğraf Ekle'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFileChange} />
          </div>

          <button type="submit" disabled={sending} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg,#059669,#10b981)', border: 'none', borderRadius: 14, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 16px rgba(16,185,129,0.3)', opacity: sending ? 0.7 : 1, fontFamily: 'inherit' }}>
            {sending ? <div className="spinner" /> : <CheckSquare size={20} />}
            {sending ? 'Kaydediliyor...' : 'Kontrolü Kaydet'}
          </button>
        </form>
      </div>
    </div>
  );
}
